import { Form, Card, Button, message } from 'antd';
import { FormCard } from '@leedpulse-monorepo/components';
import { UserRole, ContextStatus } from '@leedpulse-monorepo/enums';
import React, { useContext, useRef, useState } from 'react';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { IAdmin } from '@leedpulse-monorepo/interfaces';
import { CopyOutlined, ShareAltOutlined } from '@ant-design/icons';
import updateAdmin, { UpdateAdminFailure } from '../../../domain/use_cases/admins/updateAdmin';
import AdminFormFields, { AdminFormFieldsRef } from './AdminFormFields';
import AdminCopyModal from './AdminCopyModal';
import AdminRefreshButton from './AdminRefreshButton';
import deleteAdmin from '../../../domain/use_cases/admins/deleteAdmin';

interface AdminDetailsProps {
  admin: IAdmin;
  onCreated?: (id: string) => void | Promise<void>;
  onUpdated?: () => void | Promise<void>;
  onDeleted?: () => void | Promise<void>;
}

const AdminDetails: React.FC<AdminDetailsProps> = ({ admin, onCreated, onUpdated, onDeleted }) => {
  const authContext = useContext(AuthenticationContext);

  const [form] = Form.useForm<IAdmin>();
  const formFieldsRef = useRef<AdminFormFieldsRef>(null);
  const [status, setStatus] = useState<ContextStatus>(ContextStatus.IDLE);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState<IAdmin["id"] | null>(null);

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner].includes(
      authContext.authentication?.role
    );

  const copyShareLink = () => {
    const shareLink = `${window.location.origin}${window.location.pathname}?open_admins=${admin.id}`;

    navigator.clipboard
      .writeText(shareLink)
      .then(() => {
        message.success('Lien de la configuration copié dans le presse-papiers');
      })
      .catch(() => {
        message.error("Échec de la copie du lien");
      });
  };

  const handleSubmit = async () => {
    if (!authContext.authentication) {
      message.error('Vous devez être connecté pour effectuer cette action');
      return;
    }

    setStatus(ContextStatus.LOADING);

    try {
      // getPayload uses tabSchema as structure guide so removed keys are excluded
      const payload = formFieldsRef.current?.getPayload() ?? form.getFieldsValue(true);

      // `admin` is the doc as this page opened it: a grid saved by someone else since is refused
      await updateAdmin(payload, authContext.authentication, admin);
      message.success('Configuration mise à jour avec succès');
      setStatus(ContextStatus.SUCCESS);
      await onUpdated?.();
    } catch (error) {
      console.error(error);
      // The grid validation names the countries to fix and the conflict names its author, anything else stays generic
      const bGridError = error instanceof UpdateAdminFailure && ['invalid-outbound-pricing', 'outbound-pricing-conflict'].includes(error.code);
      message.error(bGridError ? error.message : 'Erreur lors de la mise à jour de la configuration', bGridError ? 8 : undefined);
      setStatus(ContextStatus.ERROR);
    }
  };

  return admin === null ? (
    <Card loading />
  ) : (
    <>
      <FormCard
        title={admin.id}
        onDeleted={
          () => deleteAdmin(admin.id).then(async () => {
            message.success('Configuration supprimée avec succès');
            await onDeleted?.();
          }).catch(
            () => message.error('Erreur lors de la suppression de la configuration')
          )
        }
        onUpdated={
          isReadOnly ? undefined : handleSubmit
        }
        isUpdateLoading={status === ContextStatus.LOADING}
        headerActions={[
          <Button
            key="share"
            icon={<ShareAltOutlined />}
            onClick={copyShareLink}
            style={{ marginRight: "1rem" }}
          >
            Partager
          </Button>,
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => setIsCopyModalOpen(admin.id)}
            style={{ marginRight: "1rem" }}
          >
            Copier
          </Button>,
          <AdminRefreshButton key="refresh" env={admin.id} style={{ marginRight: "1rem", marginLeft: "unset" }} />,
        ]}>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{ ...admin }}
        >
          <AdminFormFields ref={formFieldsRef} form={form} isReadOnly={isReadOnly} />
        </Form>
      </FormCard>

      <AdminCopyModal open={isCopyModalOpen !== null} onCancel={() => setIsCopyModalOpen(null)} baseAdmin={admin} onCopied={onCreated} />
    </>
  );
};

export default AdminDetails;
